import { useEffect, useId, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import { format, isValid, parse, parseISO } from "date-fns";
import { kk, ru } from "date-fns/locale";
import style from "../EventsAudit/EventAuditList.module.css";

import Calendar from "../../shared/Ui/Calendar/Calendar";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import {
  AccountCard,
  AccountOperationsMonth,
  exportAccountOperationsSummary,
  getAccountOperationsSummary,
} from "../../common/api/billingApi";
import { downloadXlsxBlob, formatBillingAmount } from "./billingUtils";
import { normalizedLocale } from "../../common/libs/normalizedLocale";

const formatCurrency = formatBillingAmount;

// Название месяца на языке интерфейса: kk — «шілде 2026», ru — «июль 2026»
const formatPeriodLabel = (period: string, language: string) => {
  const parsed = parse(`${period}-01`, "yyyy-MM-dd", new Date());
  if (isNaN(parsed.getTime())) return period;
  const locale = normalizedLocale(language) === "kk" ? kk : ru;
  return format(parsed, "LLLL yyyy", { locale });
};

// Бэк отдаёт либо дату (2026-10-02), либо дату-время с микросекундами (2026-10-02T09:01:56.312901)
const formatDayLabel = (date: string) => {
  const parsed = parseISO(date);
  if (!isValid(parsed)) return date;
  return format(parsed, date.includes("T") ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy");
};

const downloadBlob = downloadXlsxBlob;

const PaymentOrderCell = ({ value }: { value?: string }) => {
  const text = value?.trim() ? value : "-";
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const tooltipId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el || text === "-") {
      setIsOverflowed(false);
      return;
    }

    const checkOverflow = () => {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text]);

  return (
    <>
      <span
        ref={ref}
        className="inline-block max-w-[220px] truncate align-middle"
        {...(isOverflowed
          ? {
              "data-tooltip-id": tooltipId,
              "data-tooltip-content": text,
            }
          : {})}
      >
        {text}
      </span>
      {isOverflowed && <Tooltip id={tooltipId} place="top" />}
    </>
  );
};

type Props = {
  accountId: number;
  account?: AccountCard | null;
  // Меняется после пополнения счёта — отчёт перезагружается с текущим фильтром
  reloadKey?: number;
};

function AccountOperationsReport({ accountId, account, reloadKey }: Props) {
  const { t, i18n } = useTranslation();
  const [months, setMonths] = useState<AccountOperationsMonth[]>([]);
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [appliedPeriod, setAppliedPeriod] = useState<{
    dateFrom?: string;
    dateTo?: string;
  }>({});

  const formatApiDate = (date?: Date) =>
    date ? format(date, "yyyy-MM-dd") : undefined;

  const loadReport = async (dateFrom?: string, dateTo?: string) => {
    setIsLoading(true);
    try {
      const response = await getAccountOperationsSummary(
        accountId,
        dateFrom,
        dateTo,
        1,
        100,
      );
      setMonths(response?.months ?? []);
    } catch (error) {
      console.error("Error loading operations summary:", error);
      setMonths([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReport(appliedPeriod.dateFrom, appliedPeriod.dateTo);
  }, [accountId, reloadKey]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePeriod = (period: string) => {
    setExpandedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(period)) {
        next.delete(period);
      } else {
        next.add(period);
      }
      return next;
    });
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await exportAccountOperationsSummary(
        accountId,
        appliedPeriod.dateFrom,
        appliedPeriod.dateTo,
      );
      const accountNumber = account?.accountNumber ?? accountId;
      downloadBlob(blob, `Движение средств ${accountNumber}.xlsx`);
    } catch (error) {
      console.error("Error exporting operations:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
      setIsExportOpen(false);
    }
  };

  const renderFilter = () => (
    <Formik
      initialValues={{ dateFrom: "", dateTo: "" }}
      onSubmit={() => {
        const dateFrom = formatApiDate(startDate);
        const dateTo = formatApiDate(endDate);
        setAppliedPeriod({ dateFrom, dateTo });
        setIsFilterOpen(false);
        loadReport(dateFrom, dateTo);
      }}
    >
      {() => (
        <Form className="max-w-[400px] w-full">
          <div className="flex flex-row align-center justify-between">
            <p className="my-3 text-lg font-semibold">
              {t("documents_upload.filters")}
            </p>
            <button type="button" onClick={() => setIsFilterOpen(false)}>
              <img src="/icons/close.svg" alt="close" />
            </button>
          </div>
          <div className={style.inputField}>
            <div className={style.labelAndButtonWrapper}>
              <label className={style.inputLabel}>{t("billing.dateFrom")}</label>
            </div>
            <Calendar
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              showMonthYearSelect
            />
          </div>
          <div className="flex flex-row gap-[10px] justify-end mt-2">
            <button
              type="submit"
              className="bg-green text-white font-semibold py-2 px-4 rounded"
            >
              {t("mainButtons3.apply")}
            </button>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => {
                setStartDate(undefined);
                setEndDate(undefined);
                setAppliedPeriod({});
                setIsFilterOpen(false);
                loadReport();
              }}
            >
              {t("mainButtons3.reset")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="flex flex-col">
      <div className="bg-white rounded-xl shadow-md">
        <div className="p-5 flex flex-row items-center justify-between border-b border-gray-200">
          <div className="relative" ref={exportMenuRef}>
            <button
              type="button"
              className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
              disabled={isExporting}
              onClick={() => setIsExportOpen((prev) => !prev)}
            >
              {t("mainButtons.download")}
            </button>
            {isExportOpen && (
              <div className="absolute left-0 mt-2 min-w-[120px] bg-white rounded-lg shadow-lg z-50">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={handleExportExcel}
                >
                  Excel
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-[10px]">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsFilterOpen(true);
              }}
              className="bg-green text-white font-semibold py-2 px-4 rounded"
              data-tooltip-id="billing-operations-filter"
            >
              {t("mainButtons3.filter")}
            </button>
            <button type="button" onClick={() => loadReport(appliedPeriod.dateFrom, appliedPeriod.dateTo)}>
              <img src="/icons/update.svg" alt="update" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-b-xl">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  №
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.reportPeriod")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.balanceIn")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.topUp")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.paymentOrder")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.writeOff")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.balanceOut")}
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center py-5 text-gray-500">
                    {t("billing.loading")}
                  </td>
                </tr>
              </tbody>
            ) : months.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <EmptyListPlaceholderContent title={t("billing.noData")} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {months.map((month, monthIndex) => {
                  const isExpanded = expandedPeriods.has(month.period);
                  const monthRowNumber = monthIndex + 1;

                  const rows = [
                    <tr
                      key={`month-${month.period}`}
                      className="cursor-pointer hover:bg-gray-50 bg-white"
                      onClick={() => togglePeriod(month.period)}
                    >
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600 font-medium">
                        {monthRowNumber}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-700 text-left font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={classNames(
                              "inline-block text-green font-bold text-base leading-none transition-transform duration-200 ease-in-out",
                              isExpanded && "rotate-90",
                            )}
                          >
                            ›
                          </span>
                          {formatPeriodLabel(month.period, i18n.language)}
                        </span>
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatCurrency(month.balanceIn)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatCurrency(month.topUpAmount)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        -
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatCurrency(month.writeOffAmount)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatCurrency(month.balanceOut)}
                      </td>
                    </tr>,
                  ];

                  if (isExpanded) {
                    month.days?.forEach((day) => {
                      rows.push(
                        <tr
                          key={`day-${month.period}-${day.date}`}
                          className="bg-slate-100/80 hover:bg-slate-100"
                        >
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-400">
                            &nbsp;
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600 text-left">
                            <span className="inline-flex items-center gap-2 pl-8 border-l-2 border-green/40 ml-3">
                              <span className="w-1.5 h-1.5 rounded-full bg-green shrink-0" />
                              {formatDayLabel(day.date)}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600">
                            {formatCurrency(day.balanceIn)}
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600">
                            {formatCurrency(day.topUpAmount)}
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600">
                            <PaymentOrderCell value={day.paymentOrder} />
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600">
                            {formatCurrency(day.writeOffAmount)}
                          </td>
                          <td className="px-5 py-3 text-sm border-b border-gray-200 text-gray-600">
                            {formatCurrency(day.balanceOut)}
                          </td>
                        </tr>,
                      );
                    });
                  }

                  return rows;
                })}
              </tbody>
            )}
          </table>
        </div>
      </div>

      <div className="z-[1000] relative">
        <Tooltip
          isOpen={isFilterOpen}
          id="billing-operations-filter"
          className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>{renderFilter()}</div>
        </Tooltip>
      </div>
    </div>
  );
}

export default AccountOperationsReport;
